import { useCallback, useEffect, useMemo, useState } from "react";
import { GroupBucket, MainCategoriesEnum, ProductNameEnum, UID } from "@shared/core";

type Key = string;
type IndexMap = Record<Key, number>;

const makeKey = (
  product: ProductNameEnum,
  category: MainCategoriesEnum,
  groupUid: string
): Key => `${product}__${category}__${groupUid}`;

/**
 * Local hook:
 * - per product+category+group
 * - does not touch Zustand store
 * - resets local state when product changes
 */
export function useLocalPerGroupIndex(options: {
  product: ProductNameEnum;
  category: MainCategoriesEnum;
  groupBuckets: GroupBucket[];
  activeBucket: GroupBucket | null;
  selectionUid: UID | null;
}) {
  const { product, category, groupBuckets, activeBucket, selectionUid } = options;

  const [map, setMap] = useState<IndexMap>({});
  const prevProductRef = useMemo(() => ({ current: product }), [] as any);

  // Reset local state when product changes
  useEffect(() => {
    // Using a ref-like object stored in memo to avoid extra imports
    if (prevProductRef.current !== product) {
      prevProductRef.current = product;
      setMap({});
    }
  }, [product, prevProductRef]);

  const activeKey = useMemo(() => {
    if (!activeBucket) return null;
    return makeKey(product, category, activeBucket.relation.uid);
  }, [product, category, activeBucket]);

  // Initialize index for a group once (per activeKey), based on selectionUid
  useEffect(() => {
    if (!activeBucket || !activeKey) return;

    setMap((prev) => {
      if (activeKey in prev) return prev;

      let initialIndex = 0;
      if (selectionUid) {
        const found = activeBucket.items.findIndex((i) => i.uid === selectionUid);
        if (found >= 0) initialIndex = found;
      }

      return {
        ...prev,
        [activeKey]: initialIndex,
      };
    });
  }, [activeBucket, activeKey, selectionUid]);

  const activeIndex = useMemo(() => {
    if (!activeKey) return 0;
    if (activeKey in map) return map[activeKey];

    // fall back to the selection-derived index until the init effect fills the map
    if (selectionUid && activeBucket) {
      const found = activeBucket.items.findIndex((i) => i.uid === selectionUid);
      if (found >= 0) return found;
    }

    return 0;
  }, [map, activeKey, selectionUid, activeBucket]);

  const setIndexForGroup = useCallback(
    (groupUid: string, nextIndex: number) => {
      const key = makeKey(product, category, groupUid);
      setMap((prev) => {
        const current = prev[key];
        if (current === nextIndex) return prev;
        return { ...prev, [key]: nextIndex };
      });
    },
    [product, category]
  );

  const getIndexForGroup = useCallback(
    (groupUid: string): number => {
      const key = makeKey(product, category, groupUid);
      return map[key] ?? 0;
    },
    [product, category, map]
  );

  return {
    activeIndex,
    setIndexForGroup,
    getIndexForGroup,
  };
}
